import { useEffect, useState } from "react";
import { checkDrugInteractions, type DrugInteraction } from "@/lib/api";

const cache = new Map<string, DrugInteraction[]>();

/**
 * Interactions between the catalog drugs on a prescription. Re-checks (debounced) whenever the
 * set of drugs changes; identical sets are served from a session cache.
 */
export function useDrugInteractions(drugIds: number[], enabled: boolean): { interactions: DrugInteraction[]; failed: boolean } {
    const key = [...new Set(drugIds)].sort((a, b) => a - b).join(",");
    const [state, setState] = useState<{ key: string; interactions: DrugInteraction[]; failed: boolean }>({ key: "", interactions: [], failed: false });

    useEffect(() => {
        const ids = key ? key.split(",").map(Number) : [];
        if (!enabled || ids.length < 2) { setState({ key, interactions: [], failed: false }); return; }
        const cached = cache.get(key);
        if (cached) { setState({ key, interactions: cached, failed: false }); return; }
        let cancelled = false;
        const timer = setTimeout(() => {
            checkDrugInteractions(ids)
                .then(rows => { cache.set(key, rows); if (!cancelled) setState({ key, interactions: rows, failed: false }); })
                .catch(() => { if (!cancelled) setState({ key, interactions: [], failed: true }); });
        }, 400);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [key, enabled]);

    // Never show results that belong to a previous set of drugs
    return state.key === key ? { interactions: state.interactions, failed: state.failed } : { interactions: [], failed: false };
}
